import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import axiosInstance from '../../config/axios';
import { toastService } from '../../services/toast';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [tokenChecking, setTokenChecking] = useState(true);
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const email = new URLSearchParams(location.search).get('email');

  // التحقق من حالة تسجيل الدخول وصحة التوكن
  useEffect(() => {
    // التحقق من وجود جلسة مسجلة
    const checkLoggedInStatus = async () => {
      try {
        // التحقق من وجود توكن في السيشن
        const token = sessionStorage.getItem('token');
        if (token) {
          // تسجيل الخروج من الحساب الحالي
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('user');
          sessionStorage.removeItem('userType');
          // لا نقوم بإعادة توجيه المستخدم، بل نسمح له بإكمال عملية إعادة تعيين كلمة المرور
        }
      } catch (err) {
        console.error('خطأ في التحقق من حالة تسجيل الدخول:', err);
      }
    };

    const checkToken = async () => {
      if (!token || !email) {
        setTokenValid(false);
        setTokenChecking(false);
        return;
      }

      try {
        // التحقق من حالة تسجيل الدخول أولاً
        await checkLoggedInStatus();
        
        const response = await axiosInstance.get(`/api/reset-password/${token}?email=${email}`);
        setTokenValid(true);
      } catch (err) {
        console.error('Token validation error:', err);
        setTokenValid(false);
      } finally {
        setTokenChecking(false);
      }
    };

    checkToken();
  }, [token, email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== passwordConfirmation) {
      toastService.error('كلمة المرور غير متطابقة');
      return;
    }

    setLoading(true);
    try {
      // إرسال طلب إعادة تعيين كلمة المرور
      
      const response = await axiosInstance.post('/api/reset-password', {
        email,
        token,
        password,
        password_confirmation: passwordConfirmation
      });

      // تم إعادة تعيين كلمة المرور بنجاح
      toastService.success('تم إعادة تعيين كلمة المرور بنجاح');
      
      // التوجيه إلى صفحة تسجيل الدخول بعد نجاح إعادة تعيين كلمة المرور
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      // حدث خطأ في إعادة تعيين كلمة المرور
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'حدث خطأ في إعادة تعيين كلمة المرور';
      toastService.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // عرض رسالة التحميل أثناء التحقق من التوكن
  if (tokenChecking) {
    return (
      <div className="h-screen grid place-items-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-700 dark:text-gray-300">جاري التحقق من صلاحية الرابط...</p>
        </div>
      </div>
    );
  }

  // عرض رسالة خطأ إذا كان التوكن غير صالح
  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 bg-white dark:bg-gray-800 p-8 rounded-xl shadow-md">
          <div className="text-center">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <svg className="w-10 h-10 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <h2 className="mt-6 text-2xl font-bold text-gray-900 dark:text-white">رابط غير صالح</h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              رابط إعادة تعيين كلمة المرور غير صالح أو انتهت صلاحيته. يرجى طلب رابط جديد.
            </p>
          </div>
          <div className="mt-8">
            <button
              onClick={() => navigate('/login')}
              className="group relative w-full flex justify-center py-2 px-6 border border-transparent text-sm font-medium rounded-full text-white bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              العودة لتسجيل الدخول
            </button>
          </div>
        </div>
      </div>
    );
  }

  // عرض نموذج إعادة تعيين كلمة المرور
  return (
    <div className="h-screen grid place-items-center overflow-hidden bg-white dark:bg-gray-900">
      {/* Background Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none section-bg">
        <motion.div
          initial={{ scale: 0.9, opacity: 0.5 }}
          animate={{ 
            scale: [0.9, 1.1, 0.9],
            opacity: [0.5, 0.7, 0.5]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/10 dark:bg-purple-500/20 rounded-full blur-3xl"
        />
        <motion.div
          initial={{ scale: 0.9, opacity: 0.5 }}
          animate={{ 
            scale: [0.9, 1.1, 0.9],
            opacity: [0.5, 0.7, 0.5]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
          className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-purple-500/10 dark:bg-blue-500/20 rounded-full blur-3xl"
        />
      </div>

      <div className="w-full max-w-md px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl p-8 rounded-[0.5rem] [border-top-right-radius:3rem] [border-bottom-left-radius:3rem] shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] dark:shadow-2xl border border-gray-300 dark:border-gray-700"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
              إعادة تعيين كلمة المرور
            </h2>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              أدخل كلمة المرور الجديدة
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="peer h-10 max-sm:h-12 w-full px-3 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-transparent focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent align-middle"
                  placeholder="أدخل كلمة المرور الجديدة"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  dir="rtl"
                  minLength={8}
                />
                <label 
                  htmlFor="password" 
                  className="absolute right-3 -top-2.5 bg-gray-50 dark:bg-gray-700 px-1 text-xs font-medium text-gray-700 dark:text-gray-300 transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-500 peer-placeholder-shown:dark:text-gray-400 peer-placeholder-shown:top-3 peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-gray-700 peer-focus:dark:text-gray-300 flex items-center"
                >
                  كلمة المرور الجديدة
                </label>
              </div>
              <div className="relative">
                <input
                  id="password_confirmation"
                  name="password_confirmation"
                  type="password"
                  required
                  className="peer h-10 max-sm:h-12 w-full px-3 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-transparent focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent align-middle"
                  placeholder="تأكيد كلمة المرور الجديدة"
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  dir="rtl"
                  minLength={8}
                />
                <label 
                  htmlFor="password_confirmation" 
                  className="absolute right-3 -top-2.5 bg-gray-50 dark:bg-gray-700 px-1 text-xs font-medium text-gray-700 dark:text-gray-300 transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-500 peer-placeholder-shown:dark:text-gray-400 peer-placeholder-shown:top-3 peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-gray-700 peer-focus:dark:text-gray-300 flex items-center"
                >
                  تأكيد كلمة المرور
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 sm:py-2 py-3 px-6 border border-transparent text-sm font-medium rounded-full text-white bg-blue-500 hover:bg-blue-600 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              {loading ? (
                <div className="w-4 sm:w-4 w-5 h-4 sm:h-4 h-5 border-2 border-white dark:border-gray-900 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                "إعادة تعيين كلمة المرور"
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default ResetPassword;
